'use client';

import { useEffect, useState } from 'react';

import {
  DEFAULT_ERASING_SPEED,
  DEFAULT_PAUSE_DURATION,
  DEFAULT_TYPING_SPEED,
  PHASES,
} from './constants';
import { getRandomIndex } from './utils';

interface UseTypingAnimationProps {
  strings?: string[];
  typingSpeed?: number; // ms per character
  pauseDuration?: number; // ms to pause after completing a string
  erasingSpeed?: number; // ms per character when erasing
  enabled?: boolean; // Whether to start the animation
}

/**
 * ### useTypingAnimation
 *
 * @description
 * Hook that creates an animated typing effect for text.
 * Cycles through an array of strings, typing each character,
 * pausing when complete, then erasing before moving to the next string.
 *
 * @example
 * const animatedText = useTypingAnimation({
 *   strings: ['Type something...', 'Ask a question...', 'Enter text here...']
 * });
 *
 * @param props - Hook configuration
 * @param props.strings - Array of strings to cycle through
 * @param props.typingSpeed - Milliseconds per character when typing (default: 25)
 * @param props.pauseDuration - Milliseconds to pause after completing a string (default: 4000)
 * @param props.erasingSpeed - Milliseconds per character when erasing (default: 10)
 * @param props.enabled - Whether to start the animation (default: true)
 * @returns The current animated text
 */
const useTypingAnimation = ({
  strings = [],
  typingSpeed = DEFAULT_TYPING_SPEED,
  pauseDuration = DEFAULT_PAUSE_DURATION,
  erasingSpeed = DEFAULT_ERASING_SPEED,
  enabled = true,
}: UseTypingAnimationProps = {}): string => {
  const [currentStringIndex, setCurrentStringIndex] = useState(() =>
    getRandomIndex(strings.length),
  );
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [phase, setPhase] = useState<(typeof PHASES)[keyof typeof PHASES]>(PHASES.TYPING);
  const [displayText, setDisplayText] = useState('');

  useEffect(() => {
    // Don't animate if disabled, no strings provided, or not enabled
    if (!enabled || strings.length === 0) {
      setDisplayText('');
      return;
    }

    const currentString = strings[currentStringIndex];
    let timeoutId: NodeJS.Timeout;

    if (phase === PHASES.TYPING) {
      // Typing phase: add characters one by one
      if (currentCharIndex < (currentString?.length ?? 0)) {
        timeoutId = setTimeout(() => {
          setCurrentCharIndex((prev) => prev + 1);
        }, typingSpeed);
      } else {
        // Finished typing, move to pausing phase
        timeoutId = setTimeout(() => {
          setPhase(PHASES.PAUSING);
        }, 0);
      }
    } else if (phase === PHASES.PAUSING) {
      // Pausing phase: wait before erasing
      timeoutId = setTimeout(() => {
        setPhase(PHASES.ERASING);
      }, pauseDuration);
    } else if (phase === PHASES.ERASING) {
      // Erasing phase: remove characters one by one
      if (currentCharIndex > 0) {
        timeoutId = setTimeout(() => {
          setCurrentCharIndex((prev) => prev - 1);
        }, erasingSpeed);
      } else {
        // Finished erasing, move to next string
        const nextIndex = (currentStringIndex + 1) % strings.length;
        setCurrentStringIndex(nextIndex);
        setPhase(PHASES.TYPING);
      }
    }

    // Update display text based on current state
    setDisplayText(currentString?.slice(0, currentCharIndex) ?? '');

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    enabled,
    strings,
    currentStringIndex,
    currentCharIndex,
    phase,
    typingSpeed,
    pauseDuration,
    erasingSpeed,
  ]);

  // Reset when strings change or when enabled becomes true
  useEffect(() => {
    if (enabled && strings.length > 0) {
      setCurrentStringIndex(getRandomIndex(strings.length));
      setCurrentCharIndex(0);
      setPhase(PHASES.TYPING);
    }
  }, [enabled, strings]);

  return displayText;
};

export default useTypingAnimation;
